import { Box, Link, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useInstallationGuide } from '../../../hooks/useInstallationGuide';
import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '../../../utils/constants';
import { checkFususDeviceForGuide } from '../../../utils/guideDeviceCheck';
import { WizardNavigation } from '../WizardNavigation';

/** Path to loading bar animation */
const LOADING_BAR_PATH = '/assets/images/installation-guide/loading-bar.gif';

/**
 * ConnectionTestStep - Slide 3.2: Testing Connection
 *
 * Performs the actual connection test and handles results:
 * - Shows loading animation while testing (30s cloud, 10s local timeout)
 * - On success: auto-redirects to Core UI App with fallback link
 * - On failure: shows failure layer with retry counter escalation
 */
export const ConnectionTestStep: React.FC = () => {
  const {
    deviceSerial,
    connectionStatus,
    connectionRetryCount,
    deviceUrl,
    setConnectionStatus,
    setDeviceUrl,
    incrementRetryCount,
    goToStep,
    isV2Route,
  } = useInstallationGuide();

  // Local state to trigger re-running the connection test
  const [retryTrigger, setRetryTrigger] = useState(0);
  const latestAttemptRef = useRef(0);

  /**
   * Run the connection test
   */
  const runConnectionTest = useCallback(async () => {
    if (!deviceSerial) {
      setConnectionStatus('unreachable');
      return;
    }

    const attemptId = ++latestAttemptRef.current;
    setConnectionStatus('testing');

    const result = await checkFususDeviceForGuide(deviceSerial, isV2Route);

    // Ignore stale results if a newer attempt started
    if (attemptId !== latestAttemptRef.current) {
      return;
    }

    if (result.status === 'cloud' || result.status === 'local') {
      // Success - set URL and status, then auto-redirect
      setDeviceUrl(result.url ?? null);
      setConnectionStatus(result.status);

      // Auto-redirect to Core UI App (matching Storyline behavior)
      if (result.url) {
        window.location.href = result.url;
      }
    } else {
      // Failure - increment retry count and show failure layer
      setConnectionStatus('unreachable');
      incrementRetryCount();
    }
  }, [deviceSerial, setConnectionStatus, setDeviceUrl, incrementRetryCount, isV2Route]);

  // Run connection test on mount and when retry is triggered
  useEffect(() => {
    runConnectionTest();
  }, [runConnectionTest, retryTrigger]);

  /**
   * Handle "Try Again" button click
   */
  const handleTryAgain = () => {
    // Trigger a new connection test
    setRetryTrigger((prev) => prev + 1);
  };

  /**
   * Handle "Go back" / "Return to Previous Step" button click
   */
  const handleGoBack = () => {
    goToStep('ready-test');
  };

  // Determine if this is the first failure attempt
  const isFirstFailure = connectionRetryCount < 2;

  // Show failure layer when unreachable
  if (connectionStatus === 'unreachable') {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 2,
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{ mb: 3, color: 'text.primary' }}
        >
          {__('Unable to Connect')}
        </Typography>

        {isFirstFailure ? (
          // First failure message
          <>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              {__(
                "We couldn't find your device. Please double-check the troubleshooting tips and try again."
              )}
            </Typography>
            <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
              {__("When you're ready, select Try Again.")}
            </Typography>
          </>
        ) : (
          // Second+ failure message with support phone
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            {__(
              "We're still having trouble finding your device. Please contact our technical support team at "
            )}
            <Link
              href={SUPPORT_PHONE_TEL}
              sx={{ fontWeight: 500, color: 'primary.main' }}
            >
              {SUPPORT_PHONE_DISPLAY}
            </Link>
            {__('.')}
          </Typography>
        )}

        <WizardNavigation
          primaryLabel={__('Try Again')}
          onPrimary={handleTryAgain}
          secondaryLabel={
            isFirstFailure
              ? __('< Go back')
              : __('Return to Previous Step')
          }
          onSecondary={handleGoBack}
        />
      </Box>
    );
  }

  // Show success state with fallback link (brief moment before redirect)
  if ((connectionStatus === 'cloud' || connectionStatus === 'local') && deviceUrl) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 2,
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{ mb: 2, color: 'text.primary' }}
        >
          {__('Connection successful!')}
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          {__('Redirecting you to the Core UI App...')}
        </Typography>

        {/* Loading indicator during redirect */}
        <Box sx={{ mb: 4 }}>
          <Box
            component="img"
            src={LOADING_BAR_PATH}
            alt={__('Redirecting...')}
            sx={{
              width: '100%',
              maxWidth: 300,
              height: 'auto',
            }}
          />
        </Box>

        {/* Fallback link */}
        <Typography variant="body2" color="text.secondary">
          {__("If you aren't redirected automatically, ")}
          <Link href={deviceUrl} sx={{ fontWeight: 500 }}>
            {__('click here')}
          </Link>
          {__('.')}
        </Typography>
      </Box>
    );
  }

  // Show loading/testing state (default)
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 2,
      }}
    >
      <Typography
        variant="h3"
        component="h1"
        sx={{ mb: 1, color: 'text.primary' }}
      >
        {__('Testing the connection . . .')}
      </Typography>

      <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
        {__('This should only take a moment.')}
      </Typography>

      {/* Loading bar animation */}
      <Box sx={{ my: 4, width: '100%', maxWidth: 300 }}>
        <Box
          component="img"
          src={LOADING_BAR_PATH}
          alt={__('Testing connection...')}
          sx={{
            width: '100%',
            height: 'auto',
          }}
        />
      </Box>
    </Box>
  );
};
